import React, { useState, useEffect } from 'react';
import { getArticles, getArticleById, createArticle, updateArticle, deleteArticle } from '../api';
import ConfirmModal from './ConfirmModal';

const TVA_OPTIONS = [0, 2.1, 5.5, 10, 20];

const UNITE_OPTIONS = [
  { value: 'C62', label: 'C62 — Unité / Pièce' },
  { value: 'HUR', label: 'HUR — Heure' },
  { value: 'DAY', label: 'DAY — Jour' },
  { value: 'MTH', label: 'MTH — Mois' },
  { value: 'KGM', label: 'KGM — Kilogramme' },
  { value: 'GRM', label: 'GRM — Gramme' },
  { value: 'MTR', label: 'MTR — Mètre' },
  { value: 'MTK', label: 'MTK — Mètre carré' },
  { value: 'LTR', label: 'LTR — Litre' },
  { value: 'SET', label: 'SET — Forfait' },
];

function Articles() {
  const [articles, setArticles] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [formData, setFormData] = useState({
    nom: '',
    description: '',
    prix_unitaire: '',
    taux_tva: 20,
    unite: 'C62',
  });
  const [alert, setAlert] = useState(null);
  const [confirm, setConfirm] = useState(null);

  useEffect(() => {
    loadArticles();
  }, []);

  const loadArticles = async () => {
    try {
      const response = await getArticles();
      setArticles(response.data);
    } catch (error) {
      showAlert('Erreur lors du chargement des articles', 'error');
    }
  };

  const showAlert = (message, type) => {
    setAlert({ message, type });
    setTimeout(() => setAlert(null), 3000);
  };

  const resetForm = () => {
    setFormData({
      nom: '',
      description: '',
      prix_unitaire: '',
      taux_tva: 20,
      unite: 'C62',
    });
    setEditingArticle(null);
  };

  const handleDelete = (article) => {
    setConfirm({
      title: 'Supprimer l\'article',
      message: `Supprimer « ${article.nom} » ? Cette action est irréversible.`,
      confirmLabel: 'Supprimer',
      variant: 'danger',
      onConfirm: async () => {
        setConfirm(null);
        try {
          await deleteArticle(article.id);
          showAlert('Article supprimé', 'success');
          loadArticles();
        } catch {
          showAlert('Erreur lors de la suppression', 'error');
        }
      },
    });
  };

  const handleEdit = async (id) => {
    try {
      const response = await getArticleById(id);
      const a = response.data;
      setEditingArticle(a);
      setFormData({
        nom: a.nom,
        description: a.description || '',
        prix_unitaire: a.prix_unitaire.toString(),
        taux_tva: a.taux_tva !== undefined && a.taux_tva !== null ? a.taux_tva : 20,
        unite: a.unite || 'C62',
      });
      setShowModal(true);
    } catch (error) {
      showAlert('Erreur lors du chargement de l\'article', 'error');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        nom: formData.nom,
        description: formData.description || '',
        prix_unitaire: parseFloat(formData.prix_unitaire),
        taux_tva: formData.taux_tva !== undefined && formData.taux_tva !== null && formData.taux_tva !== '' ? parseFloat(formData.taux_tva) : 20,
        unite: formData.unite || 'C62',
      };
      if (editingArticle) {
        await updateArticle(editingArticle.id, payload);
        showAlert('Article modifié avec succès', 'success');
      } else {
        await createArticle(payload);
        showAlert('Article créé avec succès', 'success');
      }
      setShowModal(false);
      resetForm();
      loadArticles();
    } catch (error) {
      showAlert(editingArticle ? 'Erreur lors de la modification' : 'Erreur lors de la création', 'error');
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2>Articles</h2>
        <button className="btn btn-primary" onClick={() => { resetForm(); setShowModal(true); }}>
          + Nouvel Article
        </button>
      </div>

      {alert && (
        <div className={`alert alert-${alert.type === 'error' ? 'error' : 'success'}`}>
          {alert.message}
        </div>
      )}

      <div className="card">
        <table className="table">
          <thead>
            <tr>
              <th>Nom</th>
              <th>Description</th>
              <th>Prix unitaire</th>
              <th>TVA %</th>
              <th>Unité</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {articles.length === 0 ? (
              <tr>
                <td colSpan="5" style={{ textAlign: 'center' }}>Aucun article</td>
              </tr>
            ) : (
              articles.map(article => (
                <tr key={article.id}>
                  <td>{article.nom}</td>
                  <td>{article.description || '-'}</td>
                  <td>{article.prix_unitaire != null ? Number(article.prix_unitaire).toFixed(2) : '-'} €</td>
                  <td>{article.taux_tva != null ? Number(article.taux_tva) : 20} %</td>
                  <td>{article.unite || 'C62'}</td>
                  <td>
                    <button type="button" className="btn btn-secondary btn-sm" style={{ marginRight: '8px' }} onClick={() => handleEdit(article.id)}>
                      Modifier
                    </button>
                    <button type="button" className="btn btn-danger btn-sm" onClick={() => handleDelete(article)}>
                      Supprimer
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <ConfirmModal
        open={!!confirm}
        title={confirm?.title}
        message={confirm?.message}
        confirmLabel={confirm?.confirmLabel}
        variant={confirm?.variant || 'danger'}
        onConfirm={confirm?.onConfirm}
        onCancel={() => setConfirm(null)}
      />

      {showModal && (
        <div className="modal" onClick={() => { setShowModal(false); resetForm(); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingArticle ? 'Modifier l\'article' : 'Nouvel article'}</h2>
              <button className="close-btn" onClick={() => { setShowModal(false); resetForm(); }}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Nom *</label>
                <input
                  type="text"
                  required
                  value={formData.nom}
                  onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Prix unitaire (€) *</label>
                <input
                  type="number"
                  step="0.01"
                  required
                  value={formData.prix_unitaire}
                  onChange={(e) => setFormData({ ...formData, prix_unitaire: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Taux TVA (%)</label>
                <select
                  value={formData.taux_tva}
                  onChange={(e) => setFormData({ ...formData, taux_tva: parseFloat(e.target.value) })}
                >
                  {TVA_OPTIONS.map(t => (
                    <option key={t} value={t}>{t} %</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Unité (code UN/ECE)</label>
                <select
                  value={formData.unite}
                  onChange={(e) => setFormData({ ...formData, unite: e.target.value })}
                >
                  {UNITE_OPTIONS.map(u => (
                    <option key={u.value} value={u.value}>{u.label}</option>
                  ))}
                </select>
              </div>
              <div className="actions">
                <button type="submit" className="btn btn-primary">{editingArticle ? 'Enregistrer' : 'Créer'}</button>
                <button type="button" className="btn btn-secondary" onClick={() => { setShowModal(false); resetForm(); }}>
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Articles;

